import React, { useState, useEffect } from 'react';
import { Tv, Save, Loader2, CheckCircle, AlertCircle, Plug, ToggleLeft, ToggleRight, FlaskConical, List, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const Toggle = ({ value, onChange, label, desc, color = '#00FF88', testid }) => (
  <div className="flex items-center justify-between p-4 bg-[#222226] rounded-lg">
    <div className="pr-4">
      <span className="text-white font-medium">{label}</span>
      {desc && <p className="text-[#6B6B6E] text-xs mt-1">{desc}</p>}
    </div>
    <button type="button" onClick={() => onChange(!value)} data-testid={testid}>
      {value ? <ToggleRight className="h-8 w-8" style={{ color }} /> : <ToggleLeft className="h-8 w-8 text-[#6B6B6E]" />}
    </button>
  </div>
);

const GoldenOTTSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false, auto_on_payment: false, test_mode: true,
    year_package_id: 5, trial_package_id: 30, template_id: 2,
    dns_domain_id: 241, is_adult: false, m3u_output_type: 'mpegts'
  });
  const [options, setOptions] = useState({ packages: [], domains: [], templates: [] });
  const [apiConfigured, setApiConfigured] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, oRes] = await Promise.all([
        api.get('/admin/settings/goldenott'),
        api.get('/admin/goldenott/options').catch(() => ({ data: { packages: [], domains: [], templates: [] } }))
      ]);
      const { api_configured, ...s } = sRes.data;
      setSettings(prev => ({ ...prev, ...s }));
      setApiConfigured(!!api_configured);
      setOptions(oRes.data || { packages: [], domains: [], templates: [] });
    } catch (e) {
      setError("Impossible de charger les paramètres GoldenOTT");
    } finally { setLoading(false); }
  };

  const testConnection = async () => {
    setTesting(true); setError(''); setSuccess(''); setProfile(null);
    try {
      const res = await api.get('/admin/goldenott/test');
      setProfile(res.data.profile);
      setSuccess('Connexion réussie à GoldenOTT');
    } catch (e) {
      setError(e.response?.data?.detail || 'Échec de connexion à GoldenOTT');
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.post('/admin/settings/goldenott', settings);
      setSuccess('Paramètres GoldenOTT enregistrés');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const set = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));

  const [syncing, setSyncing] = useState(false);
  const syncContentLists = async () => {
    setSyncing(true); setError(''); setSuccess('');
    try {
      const res = await api.post('/admin/goldenott/sync-content-lists');
      set('content_lists', res.data.content_lists || []);
      setSuccess('Templates synchronisés. Activez les listes à proposer puis enregistrez.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Échec de la synchronisation des templates');
    } finally { setSyncing(false); }
  };

  const updateContentList = (idx, key, value) => {
    setSettings(prev => {
      const lists = [...(prev.content_lists || [])];
      lists[idx] = { ...lists[idx], [key]: value };
      return { ...prev, content_lists: lists };
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#FFB800]" /></div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="goldenott-settings">
      {success && (
        <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 flex items-center space-x-3">
          <CheckCircle className="h-5 w-5 text-[#00FF88]" /><span className="text-[#00FF88]">{success}</span>
        </div>
      )}
      {error && (
        <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-[#FF3B5C]" /><span className="text-[#FF3B5C]">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connexion & Activation */}
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <h3 className="text-xl font-bold text-white mb-2 flex items-center space-x-2">
            <Tv className="h-6 w-6 text-[#FFB800]" /><span>GoldenOTT — Connexion</span>
          </h3>
          <p className="text-[#B8B8BA] text-sm mb-4">
            Génération automatique des accès IPTV (lignes M3U) via l'API GoldenOTT.
          </p>

          <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold mb-4 ${apiConfigured ? 'bg-[#00FF88]/20 text-[#00FF88]' : 'bg-[#FF3B5C]/20 text-[#FF3B5C]'}`}>
            {apiConfigured ? '✓ Clé API configurée' : '✗ Clé API manquante'}
          </div>

          <button onClick={testConnection} disabled={testing}
            className="w-full bg-[#222226] border border-[#FFB800]/40 text-[#FFB800] py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 hover:bg-[#FFB800]/10 transition-colors disabled:opacity-50 mb-4"
            data-testid="goldenott-test-btn">
            {testing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plug className="h-5 w-5" />}
            <span>Tester la connexion</span>
          </button>

          {profile && (
            <div className="bg-[#0A0A0B] p-4 rounded-lg border border-[#00FF88]/30 text-sm space-y-1" data-testid="goldenott-profile">
              <p className="text-[#B8B8BA]">Compte : <span className="text-white font-mono">{profile.username}</span></p>
              <p className="text-[#B8B8BA]">Rôle : <span className="text-white">{profile.role}</span></p>
              <p className="text-[#B8B8BA]">Crédits : <span className="text-[#00FF88] font-bold">{profile.credit}</span></p>
            </div>
          )}

          <div className="space-y-3 mt-4">
            <Toggle value={settings.enabled} onChange={v => set('enabled', v)} testid="goldenott-enabled-toggle"
              label="Activer l'intégration GoldenOTT" desc="Permet la génération des accès via GoldenOTT" />
            <Toggle value={settings.auto_on_payment} onChange={v => set('auto_on_payment', v)} testid="goldenott-auto-toggle"
              label="Générer automatiquement au paiement" desc="Crée la ligne dès qu'un client paie (sinon génération manuelle)" />
            <div className={`p-4 rounded-lg border ${settings.test_mode ? 'bg-[#FFB800]/10 border-[#FFB800]' : 'bg-[#222226] border-[#3A3A3D]'}`}>
              <div className="flex items-center justify-between">
                <div className="pr-4 flex items-start gap-2">
                  <FlaskConical className="h-5 w-5 text-[#FFB800] mt-0.5 shrink-0" />
                  <div>
                    <span className="text-white font-medium">Mode TEST (essai 24H)</span>
                    <p className="text-[#FFB800] text-xs mt-1">
                      Génère un essai 24H gratuit au lieu d'un abonnement 1 an. <strong>Gardez activé pour tester</strong> sans consommer de crédits.
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => set('test_mode', !settings.test_mode)} data-testid="goldenott-testmode-toggle">
                  {settings.test_mode ? <ToggleRight className="h-8 w-8 text-[#FFB800]" /> : <ToggleLeft className="h-8 w-8 text-[#6B6B6E]" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Configuration packages / domaine */}
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <h3 className="text-xl font-bold text-white mb-4">Configuration par défaut</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">Package 1 an (production)</label>
              <select value={settings.year_package_id} onChange={e => set('year_package_id', parseInt(e.target.value))}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FFB800] focus:outline-none"
                data-testid="goldenott-year-package-select">
                {options.packages.map(p => (
                  <option key={p.id} value={p.id}>{p.package_name || p.name} (#{p.id})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">Package essai (mode test)</label>
              <select value={settings.trial_package_id} onChange={e => set('trial_package_id', parseInt(e.target.value))}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FFB800] focus:outline-none"
                data-testid="goldenott-trial-package-select">
                {options.packages.map(p => (
                  <option key={p.id} value={p.id}>{p.package_name || p.name} (#{p.id})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">Template de bouquets</label>
              <select value={settings.template_id} onChange={e => set('template_id', parseInt(e.target.value))}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FFB800] focus:outline-none"
                data-testid="goldenott-template-select">
                {options.templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name} (#{t.id})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">Domaine DNS</label>
              <select value={settings.dns_domain_id} onChange={e => set('dns_domain_id', parseInt(e.target.value))}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FFB800] focus:outline-none font-mono"
                data-testid="goldenott-domain-select">
                {options.domains.map(d => (
                  <option key={d.id} value={d.id}>{d.domain}{d.is_default ? ' (défaut)' : ''} (#{d.id})</option>
                ))}
              </select>
            </div>
            <Toggle value={settings.is_adult} onChange={v => set('is_adult', v)} color="#FF3B5C" testid="goldenott-adult-toggle"
              label="Inclure les bouquets adultes" desc="Désactivé = contenu adulte exclu" />
          </div>
        </div>
      </div>

      {/* Listes de contenu proposées aux clients */}
      <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border" data-testid="content-lists-config">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <h3 className="text-xl font-bold text-white flex items-center space-x-2">
            <List className="h-6 w-6 text-[#FFB800]" /><span>Listes de contenu (bouquets) proposées aux clients</span>
          </h3>
          <button onClick={syncContentLists} disabled={syncing}
            className="bg-[#222226] border border-[#FFB800]/40 text-[#FFB800] px-4 py-2 rounded-lg font-semibold flex items-center justify-center space-x-2 hover:bg-[#FFB800]/10 transition-colors disabled:opacity-50 text-sm"
            data-testid="sync-content-lists-btn">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span>Synchroniser les templates GoldenOTT</span>
          </button>
        </div>
        <p className="text-[#6B6B6E] text-xs mb-4">
          Activez les listes que le client pourra choisir avant paiement et r&eacute;digez une description. (L'API ne propose
          que des templates par pays, pas de bouquets individuels.)
        </p>

        {(!settings.content_lists || settings.content_lists.length === 0) ? (
          <p className="text-[#FFB800] text-sm">Cliquez sur &laquo; Synchroniser &raquo; pour r&eacute;cup&eacute;rer les listes disponibles.</p>
        ) : (
          <div className="space-y-3">
            {settings.content_lists.map((l, idx) => (
              <div key={l.template_id} className={`p-4 rounded-lg border ${l.enabled ? 'border-[#FFB800]/50 bg-[#FFB800]/5' : 'border-[#3A3A3D] bg-[#222226]'}`} data-testid={`content-list-row-${l.template_id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div>
                    <span className="text-white font-semibold">{l.name}</span>
                    <span className="text-[#6B6B6E] text-xs ml-2">#{l.template_id}</span>
                    {l.has_adult && <span className="ml-2 text-[10px] bg-[#FF3B5C]/20 text-[#FF3B5C] px-2 py-0.5 rounded-full">adulte dispo</span>}
                  </div>
                  <button type="button" onClick={() => updateContentList(idx, 'enabled', !l.enabled)} data-testid={`content-list-toggle-${l.template_id}`}>
                    {l.enabled ? <ToggleRight className="h-7 w-7 text-[#FFB800]" /> : <ToggleLeft className="h-7 w-7 text-[#6B6B6E]" />}
                  </button>
                </div>
                <input
                  type="text"
                  value={l.description || ''}
                  onChange={(e) => updateContentList(idx, 'description', e.target.value)}
                  className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FFB800] focus:outline-none"
                  placeholder="Description affich&eacute;e au client (ex: Cha&icirc;nes FR + sport + cin&eacute;...)"
                  data-testid={`content-list-desc-${l.template_id}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving}
        className="btn-primary px-8 flex items-center space-x-2 disabled:opacity-50" data-testid="goldenott-save-btn">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
        <span>Enregistrer les paramètres GoldenOTT</span>
      </button>
    </motion.div>
  );
};

export default GoldenOTTSettings;
